import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Sun, Moon, FolderKanban, ListTodo, AlertTriangle, Calendar } from 'lucide-react';
import { mockProjects } from '../../mock/data';

const SuperSearch = () => {
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(true);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');

  const bgColor = darkMode ? 'bg-[#0a0612]' : 'bg-gray-100';
  const cardBg = darkMode ? 'bg-[#1a1025]/80' : 'bg-white';
  const textColor = darkMode ? 'text-white' : 'text-gray-900';
  const textMuted = darkMode ? 'text-purple-300/60' : 'text-gray-500';
  const borderColor = darkMode ? 'border-purple-500/20' : 'border-gray-200';
  const inputBg = darkMode ? 'bg-[#0d0815]/60' : 'bg-gray-50';

  // Filter projects based on query
  const filteredProjects = mockProjects.filter(p => 
    p.name.toLowerCase().includes(query.toLowerCase()) ||
    p.company?.toLowerCase().includes(query.toLowerCase())
  );

  const categories = [
    { id: 'all', name: 'All', icon: Search },
    { id: 'projects', name: 'Projects', icon: FolderKanban },
    { id: 'actions', name: 'Actions', icon: ListTodo },
    { id: 'escalations', name: 'Escalations', icon: AlertTriangle },
    { id: 'meetings', name: 'Meetings', icon: Calendar },
  ];

  return (
    <div className={`min-h-screen ${bgColor} ${textColor}`}>
      {/* Header */}
      <header className={`sticky top-0 z-40 ${cardBg} backdrop-blur-xl border-b ${borderColor} px-4 py-3`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/mgmt-app')}
              className="p-2 rounded-lg hover:bg-purple-500/10"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-lg font-semibold text-cyan-500">Super Search</h1>
          </div>
          <button 
            onClick={() => setDarkMode(!darkMode)}
            className="p-2 rounded-lg hover:bg-purple-500/10"
          >
            {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
        </div>
      </header>

      {/* Search Input */}
      <div className="p-4">
        <div className={`flex items-center gap-3 ${inputBg} rounded-xl border ${borderColor} px-4 py-3`}>
          <Search className="w-5 h-5 text-cyan-500" />
          <input
            type="text"
            placeholder="Search projects, actions, meetings..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className={`flex-1 bg-transparent outline-none ${textColor} placeholder:${textMuted}`}
            autoFocus
          />
        </div>
      </div>

      {/* Categories */}
      <div className="px-4 pb-4">
        <div className="flex gap-2 overflow-x-auto pb-1">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-2 ${
                category === cat.id
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white'
                  : darkMode ? 'bg-white/5 text-white/70' : 'bg-gray-200 text-gray-700'
              }`}
            >
              <cat.icon className="w-4 h-4" />
              {cat.name}
            </button>
          ))}
        </div>
      </div>

      {/* Results */}
      <main className="px-4 space-y-3">
        {query.length > 0 ? (
          filteredProjects.length > 0 ? (
            filteredProjects.map((project) => (
              <div 
                key={project.id}
                className={`${cardBg} rounded-xl border ${borderColor} p-4`}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${darkMode ? 'bg-cyan-500/10' : 'bg-cyan-50'}`}>
                    <FolderKanban className="w-5 h-5 text-cyan-500" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium">{project.name}</h3>
                    <p className={`text-sm ${textMuted}`}>{project.company}</p>
                    <div className="flex gap-2 mt-2">
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        project.status === 'active' ? 'bg-green-500' : 'bg-blue-500'
                      } text-white`}>
                        {project.status}
                      </span>
                      <span className={`text-xs ${textMuted}`}>
                        {project.progress}% complete
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className={`${cardBg} rounded-2xl p-8 border ${borderColor} text-center`}>
              <Search className="w-12 h-12 text-purple-500/30 mx-auto mb-4" />
              <p className={textMuted}>No results found for "{query}"</p>
            </div>
          )
        ) : (
          <div className={`${cardBg} rounded-2xl p-8 border ${borderColor} text-center`}>
            <Search className="w-12 h-12 text-cyan-500/30 mx-auto mb-4" />
            <p className={textMuted}>Start typing to search across all modules</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default SuperSearch;
